import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { BrainCircuit, Users, Zap, Rocket } from 'lucide-react';

const Services = () => {
  // Path for the main banner image (using a public Unsplash URL for robustness)
  const bannerImage = '/Images/image24.jpeg';

  // Testimonial data with public image URLs
  // Using a generic avatar placeholder service (like UI Avatars or Picsum)
  const testimonialsData = [
    {
      name: 'Markiv',
      title: 'Operations Manager at Health Innovations Group',
      quote: "The AI solutions from Deepbrik Tech have revolutionized our operations. Their expertise and support are invaluable. A truly strategic partner.",
      avatar: 'https://i.pravatar.cc/150?img=68' // Placeholder image
    },
    {
      name: 'Jessica',
      title: 'Product Manager of Nexus Tech',
      quote: "Deepbrik Tech's app development team delivered a seamless, feature-rich application on time and within budget. Fantastic experience overall!",
      avatar: 'https://i.pravatar.cc/150?img=47' // Placeholder image
    },
    {
      name: 'Carolin',
      title: 'COO of Creative Solutions',
      quote: "Impressed with Deepbrik Tech's creativity in app development. They turned our vision into reality flawlessly. Exceptional service!",
      avatar: 'https://i.pravatar.cc/150?img=33' // Placeholder image
    },
    {
      name: 'Jason Taylor',
      title: 'Marketing Director at Global Innovations',
      quote: "Deepbrik Tech's data analytics insights have been a game-changer for our marketing strategies. Their team is professional and highly skilled.",
      avatar: 'https://i.pravatar.cc/150?img=12' // Placeholder image
    }
  ];

  // Data for the industry cards
  // Keeping Unsplash URLs for industry cards as they are good general images
  const industries = [
  {
    title: 'Healthcare',
    description: 'Transforming patient care and operations with AI-powered diagnostics, data analytics, and telemedicine solutions.',
    image: 'https://images.unsplash.com/photo-1550831107-1553da8c8464?auto=format&fit=crop&w=600&q=80' // Healthcare
  },
  {
    title: 'E-commerce & Retail',
    description: 'Enhancing customer experiences with personalized recommendations, inventory management, and secure online transactions.',
    image: 'https://images.unsplash.com/photo-1542831371-29b0f74f9713?auto=format&fit=crop&w=600&q=80' // E-commerce
  },
  {
    title: 'Finance & Banking',
    description: 'Streamlining processes with AI, blockchain, and machine learning for fraud detection, risk assessment, and smarter investment strategies.',
    image: 'https://images.unsplash.com/photo-1556742031-c6961e8560b0?auto=format&fit=crop&w=600&q=80' // Finance
  },
  {
    title: 'Energy',
    description: 'Optimizing energy management, forecasting, and sustainability with IoT and data-driven AI solutions.',
    image: 'https://images.unsplash.com/photo-1509395176047-4a66953fd231?auto=format&fit=crop&w=600&q=80' // Energy
  },
  {
    title: 'Real Estate',
    description: 'Revolutionizing property management, investment decisions, and customer engagement with AI and predictive analytics.',
    image: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=600&q=80' // Real Estate
  },
//   {
//   title: 'Manufacturing',
//   description: 'Driving automation, predictive maintenance, and quality control with AI, optimizing production processes and resource allocation.',
//   image: 'https://images.unsplash.com/photo-1581090700227-1c54c81f8b89?auto=format&fit=crop&w=600&q=80' // Manufacturing
// },
{
  title: 'Automobile',
  description: 'Enhancing vehicle design, autonomous driving, and predictive maintenance through AI-powered analytics and intelligent systems.',
  image: 'https://images.unsplash.com/photo-1502877338535-766e1452684a?auto=format&fit=crop&w=600&q=80' // Automobile
}


];


  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(3); // Default for desktop
  const sliderRef = useRef(null);

  // Function to update cardsPerPage based on screen width
  const updateCardsPerPage = () => {
    if (window.innerWidth <= 768) {
      setCardsPerPage(1); // One card on mobile
    } else if (window.innerWidth <= 1024) {
      setCardsPerPage(2); // Two cards on tablet
    } else {
      setCardsPerPage(3); // Three cards on desktop
    }
  };

  useEffect(() => {
    updateCardsPerPage(); // Set initial cards per page
    window.addEventListener('resize', updateCardsPerPage); // Update on resize
    return () => window.removeEventListener('resize', updateCardsPerPage); // Cleanup
  }, []);

  const totalCards = industries.length;
  // Calculate maxIndex ensuring it's at least 0 even if totalCards is less than cardsPerPage
  const maxIndex = Math.max(0, Math.ceil(totalCards / cardsPerPage) - 1);


  const handlePrev = () => {
    setCurrentIndex((prevIndex) => {
      const newIndex = prevIndex - 1;
      return newIndex < 0 ? maxIndex : newIndex; // Loop back to end
    });
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => {
      const newIndex = prevIndex + 1;
      return newIndex > maxIndex ? 0 : newIndex; // Loop back to start
    });
  };

  return (
    <div>
      <style>{`
        body {
          margin: 0;
          font-family: 'Segoe UI', sans-serif;
        }

        .services-banner {
          height: 300px;
          /* background-image will be set dynamically via inline style */
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          color: white;
          text-align: center;
          width: 100%;
          animation: banner-zoom 15s infinite alternate;
          transition: transform 0.5s ease, filter 0.5s ease;
        }

        .services-banner:hover {
          transform: scale(1.02);
          filter: brightness(1.1);
        }

        .services-banner::before {
          content: "";
          position: absolute;
          inset: 0;
          background-color: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(2px);
        }

        // @keyframes banner-zoom {
        //   from {
        //     background-size: 110%;
        //   }
        //   to {
        //     background-size: 100%;
        //   }
        // }

        @keyframes slideDownFade {
          0% {
            opacity: 0;
            transform: translateY(-50px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .services-heading {
          position: relative;
          z-index: 1;
          font-size: 2rem;
          font-weight: bold;
          text-shadow: 0 4px 8px rgba(0, 0, 0, 0.8);
          padding: 0 1rem;
          letter-spacing: 2px;
          opacity: 0;
          animation: slideDownFade 1.5s ease-out 0.2s forwards;
        }

        .services-section {
          background-color: white;
          padding: 50px 20px;
          text-align: center;
        }

        .services-section h2 {
          color: black;
          font-size: 25px;
          margin-bottom: 20px;
        }

        .services-cards {
          display: flex;
          justify-content: center;
          gap: 20px;
          flex-wrap: wrap;
        }

        .service-card {
          background-color: #0e2147;
          padding: 30px;
          border-radius: 16px;
          width: 100%; /* Make cards full width on mobile */
          max-width: 350px; /* Limit max width for larger screens */
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          margin: 0 auto; /* Center cards horizontally */
        }

        .service-card:nth-child(3) {
          background-color:#0e2147;
        }

        .service-icon {
          font-size: 40px;
          margin-bottom: 20px;
        }

        .service-title {
          font-size: 23px;
          font-weight: bold;
          color: white;
          margin-bottom: 10px;
        }

        .service-card ul {
          list-style-type: disc;
          padding-left: 20px;
          text-align: left;
          color: white;
        }

        .service-card ul li {
          margin-bottom: 6px;
          font-size: 15px;
        }

        .passion-section {
          background-image: linear-gradient(to bottom right, white,white );
          color: white;
          padding: 50px 20px;
          text-align: center;
          border-radius: 20px;
          margin-top: 10px;
        }

        .passion-section h2 {
          font-size: 2rem;
          font-weight: bold;
          margin-bottom: 0.2rem;
          background-clip: text;
          -webkit-background-clip: text;
          color: transparent;
          background-image: linear-gradient(to right, black, black);
        }

        .passion-section p {
          font-size: 1rem;
          color: white;
          margin-bottom: 2rem;
        }

        .passion-cards {
          display: flex;
          justify-content: center;
          gap: 20px;
          flex-wrap: wrap;
        }

        .passion-card {
          background-color: #0e2147;
          backdrop-filter: blur(10px);
          border-radius: 1rem;
          padding: 1.5rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          width: 100%; /* Make cards full width on mobile */
          max-width: 350px; /* Limit max width for larger screens */
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          margin: 0 auto; /* Center cards horizontally */
        }

        .passion-card:hover {
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05);
          transform: scale(1.02);
        }

        .passion-card-icon {
          width: 2rem;
          height: 2rem;
          margin-bottom: 1rem;
        }

        .passion-card-title {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .passion-card-desc {
          color: white;
          font-size: 1rem;
        }

        .testimonials-section {
          padding: 80px 20px;
          background-color: #0e2147;
          text-align: center;
        }

        .testimonials-section h2 {
          font-size: 2rem;
          font-weight: 700;
          color: white;
          margin-bottom: 60px;
        }

        .testimonial-cards {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 40px;
        }

        .testimonial-card {
          background: white;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
          padding: 30px 25px;
          max-width: 360px;
          text-align: left;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .testimonial-avatar {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          object-fit: cover;
          margin-bottom: 20px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .testimonial-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 36px rgba(0, 0, 0, 0.1);
        }

        .testimonial-card p {
          font-size: 1rem;
          font-style: italic;
          color: black;
          line-height: 1.6;
          margin-bottom: 25px;
        }

        .author {
          font-weight: 700;
          font-size: 1rem;
          color: black;
          margin-bottom: 4px;
        }

        .author-title {
          font-size: 0.9rem;
          color: black;
        }

        /* Responsive Tweak */
        @media (max-width: 768px) {
          .testimonial-card {
            max-width: 100%;
          }
        }

        .contact-section{
          flex-direction: column;
        }
        .contact-section {
          background: white ;
          padding: 80px 20px;
          color: white;
        }

        .contact-container {
          max-width: 600px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 40px;
          padding: 40px;
          border-radius: 20px;
          background: #0e2147;
          backdrop-filter: blur(12px);
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
        }

        .contact-info h2 {
          font-size: 42px;
          margin-bottom: 20px;
          color: white;
        }

        .contact-info p {
          font-size: 18px;
          line-height: 1.7;
          color: white;
        }

        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-row {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-row input {
          flex: 1;
        }

        .contact-form input,
        .contact-form textarea {
          padding: 16px;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          color: #333;
          background: #fff;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }

        .contact-form input:focus,
        .contact-form textarea:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(75, 0, 179, 0.4);
        }

        .contact-form textarea {
          min-height: 140px;
          resize: vertical;
        }

        .contact-form button {
          background-color: transparent;
          color: white;
          border: 1px solid white;
          padding: 0.8rem 1.5rem;
          font-size: 1rem;
          cursor: pointer;
          border-radius: 5px;
          transition: 0.3s ease;
        }

        .contact-form button:hover {
          background-color: white;
          color: #0e2147; /* Changed text color to match background */
        }


        /* Responsive */
        @media (min-width: 768px) {
          .contact-container {
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
          }

          .contact-info {
            flex: 1;
            padding-right: 40px;
          }

          .contact-form {
            flex: 1;
          }

          .form-row {
            flex-direction: row;
            gap: 20px;
          }
        }
        /* Custom CSS for the Industries Section (copied from industries-we-serve-section) */
        .font-inter {
          font-family: 'Inter', sans-serif;
        }

        .industries-section-integrated { /* Renamed class to avoid conflict */
          text-align: center;
          background-color:#0e2147; /* Light background similar to the image */
          position: relative; /* Needed for absolute positioning of nav buttons */
          padding: 50px 20px; /* Added padding to match other sections */
        }

        .industries-section-integrated h2 {
          font-size: 2rem; /* Large heading */
          font-weight: 700;
          color: white;
          margin-bottom: 1rem;
        }

        .industries-section-integrated p.subtitle {
          font-size: 1.1rem;
          color: white;
          max-width: 800px;
          margin: 0 auto 3rem auto;
          line-height: 1.6;
        }

        .slider-container {
          position: relative;
          max-width: 1200px;
          margin: 0 auto;
          overflow: hidden; /* Hide cards outside the view */
        }

        .industries-slider {
          display: flex;
          transition: transform 0.5s ease-in-out; /* Smooth sliding transition */
          width: 100%; /* Ensure it takes full width */
        }

        .industry-card-wrapper {
          flex: 0 0 calc(100% / var(--cards-per-page)); /* Distribute width based on cards per page */
          padding: 0.5rem; /* Reduced spacing between cards */
          box-sizing: border-box; /* Include padding in width calculation */
        }

        .industry-card {
          background-color: white;
          border-radius: 12px; /* Rounded corners */
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1); /* Soft shadow */
          overflow: hidden;
          text-align: left;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          display: flex;
          flex-direction: column;
          position: relative;
          min-height: 100px; /* Reduced min-height for smaller card */
          height: 70%; /* Fill the wrapper height */
        }

        .industry-card:hover {
          transform: translateY(-5px); /* Slight lift on hover */
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
        }

        .industry-card-image-container {
          width: 100%;
          height: 150px; /* Reduced height for image area */
          background-size: cover;
          background-position: center;
          border-top-left-radius: 12px;
          border-top-right-radius: 12px;
          position: absolute; /* Position over the background image */
          top: 0;
          left: 0;
          z-index: 1; /* Ensure text is above this */
          opacity: 1; /* Make background image subtle */
        }

        .industry-card-content {
          padding: 1.0rem;
          position: relative; /* For z-index to work */
          z-index: 2; /* Ensure content is above the subtle image */
          color: black;
          display: flex;
          flex-direction: column;
          justify-content: flex-end; /* Push content to bottom for visual effect */
          height: 100%; /* Take full height of the card */
          padding-top: 110px; /* Adjusted padding-top for more gap with reduced image height */
        }

        .industry-card h4 {
          font-size: 1.4rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: black;
        }

        .industry-card p {
          font-size: 0.95rem;
          line-height: 1.5;
          color: black;
        }

        .slider-nav-button {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background-color: rgba(0, 0, 0, 0.5);
          color: white;
          border: none;
          padding: 10px 15px;
          cursor: pointer;
          font-size: 1.5rem;
          border-radius: 50%;
          z-index: 10;
          transition: background-color 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 50px;
          height: 50px;
        }

        .slider-nav-button:hover {
          background-color: rgba(0, 0, 0, 0.8);
        }

        .slider-nav-button.prev {
          left: -25px;
        }

        .slider-nav-button.next {
          right: -25px;
        }

        @media (max-width: 1024px) {
          .slider-nav-button.prev {
            left: 0px;
          }
          .slider-nav-button.next {
            right: 0px;
          }
        }

        @media (max-width: 768px) {
          .industries-section-integrated h2 {
            font-size: 2rem;
          }
          .industries-section-integrated p.subtitle {
            font-size: 1rem;
            margin-bottom: 2rem;
          }
          .slider-nav-button {
            width: 40px;
            height: 40px;
            font-size: 1.2rem;
            padding: 5px 10px;
          }
          .slider-nav-button.prev {
            left: 5px;
          }
          .slider-nav-button.next {
            right: 5px;
          }
        }
      `}</style>

      {/* Banner Section */}
      <section
        className="services-banner"
        style={{ backgroundImage: `url(${bannerImage})` }} // Apply background image dynamically
      >
        <h1 className="services-heading">Our Services</h1>
      </section>

      {/* Our Services Section */}
      <section className="services-section">
        <h2>Our Services</h2>
        <div className="services-cards">

          {/* AI & Machine Learning Solutions */}
          <div className="service-card">
            <div className="service-icon">🧠</div>
            <div className="service-title">AI & Machine Learning Solutions</div>
            <ul>
              <li>Custom ML Model Development</li>
              <li>Deep Learning for Vision & NLP</li>
              <li>Predictive Analytics & Forecasting</li>
              <li>Recommendation Systems</li>
              <li>Knowledge Distillation & Model Optimization</li>
              <li>ML Model Deployment (on-prem / cloud)</li>
            </ul>
          </div>
          {/* Automobile & Fleet Intelligence */}
          <div className="service-card">
        <div className="service-icon">🚚</div>
       <div className="service-title">Automobile & Fleet Intelligence </div>
        <ul >
  <li>ADAS System Design & Sensor Fusion</li>
  <li>On-Board Diagnostics & Predictive Health Monitoring</li>
  <li>Driving Behavior Classification & Anomaly Detection</li>
  <li>Vehicle Trajectory Analysis & Route Optimization</li>
</ul>

      </div>

          {/* Data Analytics & Business Intelligence */}
          <div className="service-card">
            <div className="service-icon">📊</div>
            <div className="service-title">Data Analytics & Business Intelligence</div>
            <ul>
              <li>Data Cleaning, Transformation & Visualization</li>
              <li>Real-Time & Historical Data Dashboards</li>
              <li>KPI Tracking & Custom Reports</li>
              <li>Anomaly Detection & Root Cause Analysis</li>
              <li>Big Data Solutions (Spark, Hadoop, etc.)</li>
            </ul>
          </div>

          {/* Web Application Development */}
          <div className="service-card">
            <div className="service-icon">💻</div>
            <div className="service-title">Web Application Development</div>
            <ul>
              <li>Full-Stack Web Application Development</li>
              <li>Responsive UI/UX with Modern Frameworks</li>
              <li>Backend API Integration & Database Design</li>
              <li>Cloud-native & Serverless Deployments</li>
              <li>Real-time Data Streaming Dashboards</li>
            </ul>
          </div>

          {/* Smart Traffic & Mobility Intelligence */}
          <div className="service-card">
            <div className="service-icon">🚦</div>
            <div className="service-title">Smart Traffic & Mobility Intelligence</div>
            <ul>
              <li>Traffic Flow Analysis & Prediction</li>
              <li>Vehicle Detection & Classification</li>
              <li>Automated Violation Detection (Helmet, Speed, Red-light)</li>
              <li>Smart Parking & Navigation Systems</li>
              <li>Integration with GIS & Smart City Infrastructure</li>
            </ul>
          </div>

          {/* Document & Image Intelligence */}
          <div className="service-card">
            <div className="service-icon">🖼️</div>
            <div className="service-title">Document & Image Intelligence</div>
            <ul>
              <li>Intelligent Document Processing (OCR + NLP)</li>
              <li>Medical Image Analysis (e.g., Dermatology, Radiology)</li>
              <li>Auto-tagging and Classification Systems</li>
              <li>AI-based Report Summarization</li>
            </ul>
          </div>

          {/* AI Consulting & Strategy */}
          <div className="service-card">
            <div className="service-icon">💡</div>
            <div className="service-title">AI Consulting & Strategy</div>
            <ul>
              <li>AI Feasibility Assessment</li>
              <li>End-to-End AI Roadmap Planning</li>
              <li>PoC/MVP Development</li>
              <li>Training & Workshops for Teams</li>
              <li>Technical Support & Maintenance</li>
            </ul>
          </div>

          {/* Newsletter Intelligence & Content Automation (Optional Add-on) */}
          <div className="service-card optional">
            <div className="service-icon">📧</div>
            <div className="service-title">Newsletter Intelligence & Content Automation</div>
            <ul>
              <li>AI-Driven Newsletter Creation & Scheduling</li>
              <li>Content Summarization & Personalization</li>
              <li>Engagement & Open-rate Optimization</li>
              <li>Integration with Mailchimp, Substack, etc.</li>
            </ul>
          </div>

          {/* Cloud & DevOps for AI */}
          <div className="service-card">
            <div className="service-icon">☁️</div>
            <div className="service-title">Cloud & DevOps for AI</div>
            <ul>
              <li>MLOps & Continuous Deployment</li>
              <li>Docker/Kubernetes Setup</li>
              <li>Scalable Cloud Architecture (AWS, GCP, Azure)</li>
              <li>CI/CD Pipelines for AI/ML Workflows</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Industries We Serve Section (Integrated) */}
      <section className="industries-section-integrated py-16 px-4 md:px-8 bg-gray-50 font-inter">
        <h2 className="text-4xl font-bold mb-4 text-gray-800">Industries We Serve</h2>
        <p className="subtitle text-lg text-gray-600 mb-12">
          We cater to a wide range of industries, delivering tailored AI and tech solutions to meet specific needs
        </p>

        <div className="slider-container">
          <button
            className="slider-nav-button prev"
            onClick={handlePrev}
            aria-label="Previous slide"
          >
            &#10094; {/* Left arrow */}
          </button>
          <div
            className="industries-slider"
            ref={sliderRef}
            style={{
              transform: `translateX(-${currentIndex * (100 / cardsPerPage)}%)`,
              '--cards-per-page': cardsPerPage // Pass CSS variable for dynamic width
            }}
          >
            {industries.map((industry, index) => (
              <div key={index} className="industry-card-wrapper">
                <div className="industry-card">
                  {/* Background image container */}
                  <div
                    className="industry-card-image-container"
                    style={{ backgroundImage: `url(${industry.image})` }}
                  ></div>
                  <div className="industry-card-content">
                    <h4>{industry.title}</h4>
                    <p>{industry.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button
            className="slider-nav-button next"
            onClick={handleNext}
            aria-label="Next slide"
          >
            &#10095; {/* Right arrow */}
          </button>
        </div>
      </section>

      {/* Passion Section */}
      <section className="passion-section">
        <h2>The fire that fuels our passion.</h2>
        <p>At Deepbrik Technologies, we believe in the power of:</p>
        <div className="passion-cards">
          {/* Creativity Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="passion-card"
          >
            <div className="flex items-center gap-4 mb-4">
              <BrainCircuit className="passion-card-icon text-purple-400" />
              <h3 className="passion-card-title">Creativity.</h3>
            </div>
            <p className="passion-card-desc">
              We dare to dream big and push boundaries, never settling for the ordinary.
            </p>
          </motion.div>

          {/* Collaboration Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="passion-card"
          >
            <div className="flex items-center gap-4 mb-4">
              <Users className="passion-card-icon text-blue-400" />
              <h3 className="passion-card-title">Collaboration.</h3>
            </div>
            <p className="passion-card-desc">
              We thrive on diverse perspectives and believe in the magic of working together.
            </p>
          </motion.div>

          {/* Impact Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            className="passion-card"
          >
            <div className="flex items-center gap-4 mb-4">
              <Zap className="passion-card-icon text-green-400" />
              <h3 className="passion-card-title">Impact.</h3>
            </div>
            <p className="passion-card-desc">
              We&apos;re driven by a desire to make a difference, to create brands that move
              mountains.
            </p>
          </motion.div>

          {/* Innovation Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            viewport={{ once: true }}
            className="passion-card"
          >
            <div className="flex items-center gap-4 mb-4">
              <Rocket className="passion-card-icon text-pink-400" />
              <h3 className="passion-card-title">Innovation.</h3>
            </div>
            <p className="passion-card-desc">
              We stay ahead of the curve, embracing new technologies & trends to keep
              your brand on top.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <h2>What They Say About Us</h2>
        <div className="testimonial-cards">
          {testimonialsData.map((testimonial, index) => (
            <div className="testimonial-card" key={index}>
              <img src={testimonial.avatar} alt={testimonial.name} className="testimonial-avatar" />
              <p>
                &quot;{testimonial.quote}&quot;
              </p>
              <div className="author">{testimonial.name}</div>
              <div className="author-title">{testimonial.title}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="contact-section">
        <div className="contact-container">
          <h2>Contact Us</h2>
          <form className="contact-form">
            <input type="text" placeholder="Your Name" required />
            <input type="email" placeholder="Your Email" required />
            <textarea placeholder="Your Message" required></textarea>
            <button type="submit">SEND MESSAGE</button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Services;